import * as React from "react";

import { cn } from "../../lib/utils";
import { ListRow, type ListRowProps } from "./list-row";

/**
 * A `ListRow` whose actions open out of its right edge on hover.
 *
 * The pattern every dense list in this app kept re-inventing: a row that ends
 * in some trailing meta — a code, a duration, a role picker — and also carries
 * two or three `IconButton` actions that should not be shouting at you from
 * forty rows at once.
 *
 * **Three ways to do that, and only one of them works.**
 *
 * Render the actions in the flow at `opacity-0` and every row you are *not*
 * pointing at ends in a column of dead air the width of the buttons — down a
 * long list that reads as a column with nothing in it.
 *
 * Position them absolutely over the meta and they need an opaque fill to hide
 * what is under them, and there is no such fill to be had: `--color-hover` is a
 * 4% wash, and stacking it over a guess at the row's own surface lands a step
 * off and reads as a patch seamed across the row. Worse, it *hides the meta* —
 * the task, the pull request, the role — on the one row you are actually
 * looking at, which is the row you most need it on.
 *
 * So neither. The actions live in the flow inside a **collapsed grid column**
 * (`grid-cols-[0fr]` → `[1fr]`, the one width animation CSS will interpolate),
 * clipped to nothing at rest and opening to their natural width on hover. At
 * rest they occupy zero pixels, so there is no gutter. Open, they push the meta
 * left rather than covering it, so nothing is hidden and nothing needs a
 * background. The width is the buttons' own, so it cannot drift when one is
 * added.
 *
 * The hover fill is rounded (`rounded-lg`), which is why this is a component
 * rather than a prop on `ListRow`: a row that opens out a cluster of controls
 * reads as a card you are pointing at, and a square-cornered wash running the
 * full bleed of the pane reads as a table stripe.
 *
 * ```tsx
 * <ActionRow
 *   leading={<StatusGlyph …/>}
 *   meta={<span>{duration}</span>}
 *   actions={<IconButton label="Delete" …/>}
 * >
 *   {name}
 * </ActionRow>
 * ```
 */

/**
 * Reveal timing.
 *
 * `duration-150` rather than the 100ms the rest of the system hovers at: this
 * moves a column boundary as well as fading, and a width that fast reads as a
 * jump rather than as an opening. `motion-reduce` drops the animation — the
 * actions still appear, the row just does not animate to let them in.
 */
const REVEAL =
  "transition-[grid-template-columns,opacity] duration-150 ease-out-fast motion-reduce:transition-none";

export interface ActionRowProps extends Omit<ListRowProps, "trailing"> {
  /**
   * Right-aligned trailing content — a duration, a code, a role `Select`.
   *
   * It stays visible and stays live when the actions open: they take their
   * width from the row's right edge and push this left, rather than covering
   * it. So a control belongs here as readily as a label does.
   */
  meta?: React.ReactNode;
  /**
   * The row's actions — `IconButton`s. Open out of the right edge on hover,
   * and on focus from the keyboard, which is what keeps them reachable without
   * a pointer.
   *
   * Pass `undefined` where the viewer may not act on the row at all, rather
   * than a permission wrapper that renders nothing: an empty cluster still
   * animates a gap open on every hover.
   */
  actions?: React.ReactNode;
  /** Classes for the action cluster (e.g. a wider gutter for four buttons). */
  actionsClassName?: string;
}

const ActionRow = React.forwardRef<HTMLElement, ActionRowProps>(
  ({ meta, actions, actionsClassName, className, children, ...props }, ref) => (
    <ListRow
      ref={ref}
      className={cn("rounded-lg", className)}
      trailing={
        <>
          {meta}
          {actions ? (
            <span
              className={cn(
                "grid grid-cols-[0fr]",
                /*
                  `-ml-2` cancels the trailing wrapper's own `gap-2`, and `-mr-2`
                  pulls the cluster out to 8px from the row's edge. Without the
                  first, a collapsed cell still costs 8px on each side and the
                  dead air is back in miniature; without the second the last
                  glyph sits 21px in — the row's 16px gutter plus the 5px of
                  optical padding an `sm` IconButton carries — and reads as not
                  reaching its own end.
                */
                "-mx-2",
                REVEAL,
                "group-hover:grid-cols-[1fr] group-focus-within:grid-cols-[1fr]",
                actionsClassName,
              )}
            >
              {/*
                The clip. `overflow-hidden` is what makes the 0fr column hide
                its content instead of overflowing it, and it is why the buttons
                appear to slide out of the edge rather than fade in place.

                `pl-2` rides inside the clip, so it is part of what opens: a gap
                between the meta and the first glyph when open, and nothing at
                all when closed.
              */}
              <span
                className={cn(
                  "flex items-center overflow-hidden pl-2",
                  "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                  REVEAL,
                )}
              >
                {actions}
              </span>
            </span>
          ) : null}
        </>
      }
      {...props}
    >
      {children}
    </ListRow>
  ),
);
ActionRow.displayName = "ActionRow";

export { ActionRow };
